import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import mainService from '../services/mainService';
import { useToast } from '../contexts/ToastContext';
import './Auth.css';

/** API may return 200 with JSON `null` on failure, or a user DTO; newer APIs often use camelCase. */
const unwrapLoginUser = (raw) => {
  if (raw == null || typeof raw !== 'object') return null;
  return raw.Data ?? raw.data ?? raw.Result ?? raw.result ?? raw;
};

const getUserRole = (user) => user?.Role ?? user?.role ?? user?.UserRole ?? user?.userRole ?? '';

const getAuthToken = (user) => user?.Token ?? user?.token ?? user?.AccessToken ?? user?.accessToken ?? '';

const isCustomerUser = (user) => {
  const r = getUserRole(user);
  return String(r).toLowerCase() === 'customer';
};

/** Allow login if role is customer, or API omits Role but returns a customer id (web store account). */
const isWebCustomerLogin = (user) => {
  if (!user) return false;
  const role = String(getUserRole(user)).toLowerCase();
  if (role && ['vendor', 'admin', 'employee'].includes(role)) {
    return false;
  }
  if (isCustomerUser(user)) return true;
  const cid = user?.CustomerId ?? user?.customerId;
  return typeof cid === 'number' && cid > 0;
};

const Login = () => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    emailId: '',
    password: '',
    rememberMe: false
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const payload = { UserName: formData.emailId, Password: formData.password };
      const response = await mainService.login(payload);
      // WebLogin often returns HTTP 200 with body `null` for wrong password — not an axios error
      const user = unwrapLoginUser(response);

      if (user && isWebCustomerLogin(user)) {
        localStorage.setItem('userData', JSON.stringify(user));
        localStorage.setItem('authToken', getAuthToken(user));

        if (formData.rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberMe');
        }

        showToast('Logged in successfully!', 'success');
        setFormData({ emailId: '', password: '', rememberMe: false });
        navigate('/');
      } else if (user && getUserRole(user) && !isWebCustomerLogin(user)) {
        setError('Access denied. Only customers can login to this application.');
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } catch (error) {
      console.error('Login failed:', error);
      
      if (error.response) {
        const errorMessage = error.response.data?.message || error.response.data?.error || 'Login failed. Please check your credentials.';
        setError(errorMessage);
      } else if (error.request) {
        setError('Network error. Please check your internet connection.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-content">
        {/* Left Side - Informational */}
        <div className="auth-info">
          <div className="auth-info-content">
            <div className="auth-logo">
              <img src="/images/logo.png" alt="Eilir" className="logo-image" />
            </div>
            
            <h2 className="auth-title">Welcome Back!</h2>
            <p className="auth-subtitle">
              Sign in to your account to continue shopping and manage your orders
            </p>
            
            <div className="auth-features">
              <div className="feature-item">
                <span className="feature-icon">🚀</span>
                <div className="feature-text">
                  <h4>Fast & Secure</h4>
                  <p>Quick login with enhanced security</p>
                </div>
              </div>
              
              <div className="feature-item">
                <span className="feature-icon">💳</span>
                <div className="feature-text">
                  <h4>Easy Payments</h4>
                  <p>Multiple payment options available</p>
                </div>
              </div>
              
              <div className="feature-item">
                <span className="feature-icon">📦</span>
                <div className="feature-text">
                  <h4>Track Orders</h4>
                  <p>Real-time order tracking and updates</p>
                </div>
              </div>
            </div>
            
            <div className="auth-footer">
              <p>Don't have an account? <Link to="/register" className="auth-link">Sign up here</Link></p>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="auth-form-container">
          <div className="auth-form-card">
            <div className="form-header">
              <h2>Sign In</h2>
              <p>Enter your credentials to access your account</p>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="emailId">Email Address</label>
                <input
                  type="email"
                  id="emailId"
                  name="emailId"
                  value={formData.emailId}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter your password"
                  required
                />
              </div>

              <div className="form-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span className="checkbox-custom"></span>
                  Remember me
                </label>
                
                <Link to="/forgot-password" className="forgot-link">
                  Forgot password?
                </Link>
              </div>

              <button 
                type="submit" 
                className={`auth-btn auth-btn-signin ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner"></span>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
